/**
 * SceneCrossfade — enchaîne les scènes avec fondu entrant/sortant (opacity only).
 * Évite le remount brutal de RoleScene à chaque index (illustration + particules).
 */

import { useEffect, useRef, useState } from 'react';
import RoleScene from './RoleScene.jsx';

/** Aligné sur --dur-slow (variables.css / motion.css). */
const CROSSFADE_MS = 320;

/**
 * @param {{ scene: object|null }} props
 */
export default function SceneCrossfade({ scene }) {
  const sceneId = scene?.id ?? '_empty';
  const [layers, setLayers] = useState(() => [{ id: sceneId, scene, phase: 'enter' }]);
  const prevIdRef = useRef(sceneId);

  useEffect(() => {
    if (sceneId === prevIdRef.current) return undefined;

    const outgoingId = prevIdRef.current;
    prevIdRef.current = sceneId;

    setLayers((prev) => {
      const outgoing = prev.find((l) => l.id === outgoingId) ?? prev[0];
      return [
        { id: sceneId, scene, phase: 'enter' },
        { id: outgoingId, scene: outgoing?.scene ?? scene, phase: 'exit' },
      ];
    });

    const timer = setTimeout(() => {
      setLayers([{ id: sceneId, scene, phase: 'enter' }]);
    }, CROSSFADE_MS);

    return () => clearTimeout(timer);
  }, [sceneId, scene]);

  return (
    <div className="rx-stage-stack">
      {layers.map((layer) => (
        <div
          key={`${layer.id}-${layer.phase}`}
          className={`rx-stage-layer rx-stage-layer--${layer.phase}`}
          aria-hidden={layer.phase === 'exit' ? 'true' : undefined}
        >
          <RoleScene scene={layer.scene} animateEnter={layer.phase === 'enter'} />
        </div>
      ))}
    </div>
  );
}
